import { Component, Input, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { LottieAnimationComponent } from 'src/app/@shared/components/lottie-animation/lottie-animation.component';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { FormatTextPipe } from 'src/app/@shared/pipes/formated-text.pipe';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
import {
    GEOTECHNICAL_SUMMARY_STATUS,
    GeotechnicalAnalysisService,
    GeotechnicalAnalysisSummaryListItem,
} from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';
import { GEOTECHNICAL_ANALYSIS_SVG, SVG } from 'src/app/common/svgs';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';

/**
 * Dedicated "Analysis Summary" tab extracted from the inline showAnalysisSummary
 * block that previously lived inside GeoTechnicalDashboardComponent.
 * The tab loads the latest summary on init and listens to socket status changes
 * so it stays up to date while generation is in progress.
 */
@Component({
    selector: 'app-geo-technical-analysis-summary-tab',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonComponent,
        IconComponent,
        DialogComponent,
        MenuComponent,
        LottieAnimationComponent,
        UserNameDisplayComponent,
        SanitizeHtmlPipe,
        FormatTextPipe,
        CreditsOverviewComponent,
    ],
    templateUrl: './analysis-summary-tab.component.html',
    styles: [`:host { display: block; height: 100%; width: 100%; }`],
})
export class GeoTechnicalAnalysisSummaryTabComponent implements OnInit, OnDestroy {
    /**
     * Pre-populated instructions text for the re-generate description textarea.
     * Injected by the tab config via `data.analysisSummaryDescriptionInput`.
     */
    @Input() analysisSummaryDescriptionInput = '';

    GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
    SVG = SVG;
    readonly GEOTECHNICAL_SUMMARY_STATUS = GEOTECHNICAL_SUMMARY_STATUS;

    /** Latest generated summary details (id + content) */
    latestSummaryDetails: { _id: string; summary: string } = { _id: '', summary: '' };

    /** True while a download is in progress */
    downloadLoading = signal<boolean>(false);

    /** True while the summary is being fetched or generated */
    analysisSummaryLoading = false;

    /** Current generation status received from the server / socket */
    analysisSummaryStatus: GEOTECHNICAL_SUMMARY_STATUS | null = null;

    /** Controls the re-generate description dialog visibility */
    analysisSummaryDescriptionDialogVisible = false;

    /** True when the history sub-view is open */
    openAnalysisSummaryHistory = false;

    /** History list items fetched when the user opens the history view */
    analysisSummaryHistoryList: GeotechnicalAnalysisSummaryListItem[] = [];

    /** True while the history list is being fetched */
    loadingAnalysisSummaryHistory = false;

    /** Expose the icon-source enum to the template / menu builder. */
    readonly IconSourceEnum = IconSourceEnum;

    // ── View summary dialog ────────────────────────────────────────────────
    viewDialogVisible = false;
    viewSummaryTitle = '';
    viewSummaryLoading = false;
    viewSummaryContent = '';

    // ── Rename summary dialog ──────────────────────────────────────────────
    renameDialogVisible = false;
    renameInput = '';
    private _renameTarget: GeotechnicalAnalysisSummaryListItem | null = null;

    /** Lottie animation data for the in-progress state */
    generationAnimationData: any;
    /** Lottie animation data for the loading and "In Queue" states (loading-spinner) */
    queueAnimationData: any;

    private statusSubscription!: Subscription;

    constructor(
        private dataService: GeoTechnicalAnalysisReportDataService,
        private geotechnicalAnalysisService: GeotechnicalAnalysisService,
        private socketService: SocketService,
        private toast: ToastService,
        private creditsState: UserCreditsStateService,
    ) {}

    get analysisId(): string {
        return this.dataService.analysisReportId();
    }

    get isViewer(): boolean {
        const p = this.dataService.permissions();
        return !this.dataService.isOwner() && !this.dataService.isEditor() && !p?.canComment;
    }

    /** Credits-exhausted if the backend told us directly, OR the org's agent balance is already at
     *  zero and we have no summary yet — covers the case where this summary never got enqueued
     *  because an upstream agent ran out first, so no status or socket event is ever coming. */
    get creditsExhausted(): boolean {
        return this.creditsState.isExhaustedFor(this.analysisSummaryStatus, !!this.latestSummaryDetails.summary);
    }

    ngOnInit(): void {
        // Load the latest summary immediately when the tab becomes visible
        this.fetchLatestAnalysisSummary();
        this.loadGenerationAnimation();
        this.loadQueueAnimation();

        // Subscribe to socket updates for live generation status
        this.statusSubscription = this.socketService
            .on<{ geotechnicalAnalysisId: string; status: GEOTECHNICAL_SUMMARY_STATUS }>(
                SOCKET_EVENTS.GEOTECHNICAL_ANALYSIS_SUMMARY_STATUS_CHANGED,
            )
            .subscribe(result => {
                const payload = result?.[0];
                if (!payload || payload.geotechnicalAnalysisId !== this.analysisId) return;

                this.analysisSummaryStatus = payload.status;
                if (payload.status === GEOTECHNICAL_SUMMARY_STATUS.IN_PROGRESS) {
                    this.analysisSummaryLoading = true;
                } else if (payload.status === GEOTECHNICAL_SUMMARY_STATUS.COMPLETED) {
                    this.fetchLatestAnalysisSummary();
                } else if (payload.status === GEOTECHNICAL_SUMMARY_STATUS.FAILED) {
                    this.analysisSummaryLoading = false;
                    this.toast.error('Analysis summary generation failed');
                } else if (payload.status === GEOTECHNICAL_SUMMARY_STATUS.CREDITS_EXHAUSTED) {
                    this.analysisSummaryLoading = false;
                    this.toast.error('Credits exhausted — analysis summary generation paused');
                }
            });
    }

    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
    }

    /** Fetches the latest (most recent) analysis summary from the server */
    fetchLatestAnalysisSummary(): void {
        if (!this.analysisId) return;
        this.analysisSummaryLoading = true;
        this.geotechnicalAnalysisService.fetchLatestAnalysisSummary(this.analysisId).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.latestSummaryDetails._id = res.data._id ?? '';
                    this.latestSummaryDetails.summary = res.data.summary ?? '';
                    this.analysisSummaryStatus = res.data.analysisSummaryStatus ?? null;
                    this.analysisSummaryLoading =
                        this.analysisSummaryStatus === GEOTECHNICAL_SUMMARY_STATUS.IN_PROGRESS;
                } else {
                    this.analysisSummaryLoading = false;
                }
            },
            error: () => {
                this.analysisSummaryLoading = false;
            },
        });
    }

    /** Triggers a re-generation using the current description input */
    onSubmitAnalysisSummaryDescription(): void {
        if (!this.analysisId) return;
        this.analysisSummaryLoading = true;
        this.analysisSummaryStatus = GEOTECHNICAL_SUMMARY_STATUS.IN_PROGRESS;
        this.analysisSummaryDescriptionDialogVisible = false;
        this.geotechnicalAnalysisService
            .regenerateAnalysisSummary(this.analysisId, this.analysisSummaryDescriptionInput)
            .subscribe({
                next: (res) => {
                    if (res.success) {
                        this.toast.success(res.message ?? 'Analysis Summary re-generated');
                    } else {
                        this.toast.error(res.message ?? 'Failed to re-generate analysis summary');
                        this.analysisSummaryLoading = false;
                    }
                },
                error: () => {
                    this.analysisSummaryLoading = false;
                    this.toast.error('Failed to re-generate analysis summary');
                },
            });
    }

    /** Opens the history sub-view and fetches the history list */
    onAnalysisSummaryHistoryClick(): void {
        if (!this.analysisId) return;
        this.loadingAnalysisSummaryHistory = true;
        this.openAnalysisSummaryHistory = true;
        this.geotechnicalAnalysisService.analysisSummaryList(this.analysisId).subscribe({
            next: (res) => {
                this.analysisSummaryHistoryList = res.success && Array.isArray(res.data) ? res.data : [];
                this.loadingAnalysisSummaryHistory = false;
            },
            error: () => {
                this.analysisSummaryHistoryList = [];
                this.loadingAnalysisSummaryHistory = false;
            },
        });
    }

    /** Downloads an analysis summary PDF by its ID */
    onDownloadAnalysisSummaryItem(summaryId: string | undefined): void {
        if (!summaryId) return;
        this.toast.info('Download in progress…');
        this.downloadLoading.set(true);
        this.geotechnicalAnalysisService.downloadAnalysisSummaryById(summaryId).subscribe({
            next: (response) => {
                const blob = response.body;
                if (!blob) {
                    this.toast.error('Failed to download analysis summary');
                    return;
                }
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = 'analysis-summary.pdf';
                if (contentDisposition) {
                    const match =
                        contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) ??
                        contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
                    if (match?.[1]) filename = decodeURIComponent(match[1].trim());
                }
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                this.downloadLoading.set(false);
                this.toast.success('Analysis summary downloaded successfully');
            },
            error: () => {
                this.downloadLoading.set(false);
                this.toast.error('Failed to download analysis summary');
            },
        });
    }

    /** Deletes an analysis summary item from the history list */
    onDeleteAnalysisSummaryItem(item: GeotechnicalAnalysisSummaryListItem): void {
        const summaryId = item._id;
        if (!summaryId) return;
        this.geotechnicalAnalysisService.deleteAnalysisSummary(summaryId).subscribe({
            next: (res) => {
                if (res.success) {
                    this.toast.success('Analysis summary deleted');
                    this.analysisSummaryHistoryList = this.analysisSummaryHistoryList.filter(
                        i => i._id !== summaryId,
                    );
                } else {
                    this.toast.error(res.message ?? 'Failed to delete analysis summary');
                }
            },
            error: () => this.toast.error('Failed to delete analysis summary'),
        });
    }

    /** Builds the per-item history menu (mirrors the AI Dashboard history menu). */
    getAnalysisSummaryMenuItems(item: GeotechnicalAnalysisSummaryListItem): CustomMenuItem[] {
        return [
            { label: 'View', icon: 'pi pi-eye', iconSource: this.IconSourceEnum.PRIMENG, callback: () => this.onViewAnalysisSummaryItem(item) },
            { label: 'Rename', icon: 'pi pi-pencil', iconSource: this.IconSourceEnum.PRIMENG, callback: () => this.onRenameAnalysisSummaryItem(item) },
            { label: 'Restore', icon: 'pi pi-history', iconSource: this.IconSourceEnum.PRIMENG, callback: () => this.onRestoreAnalysisSummaryItem(item) },
            { label: 'Download', icon: 'pi pi-download', iconSource: this.IconSourceEnum.PRIMENG, callback: () => this.onDownloadAnalysisSummaryItem(item._id) },
            { label: 'Delete', icon: 'pi pi-trash', iconSource: this.IconSourceEnum.PRIMENG, class: 'text-red-600', callback: () => this.onDeleteAnalysisSummaryItem(item) },
        ];
    }

    /** Opens a dialog rendering the selected analysis summary's markdown content. */
    onViewAnalysisSummaryItem(item: GeotechnicalAnalysisSummaryListItem): void {
        if (!item._id) return;
        this.viewSummaryTitle = item.name || 'Analysis Summary';
        this.viewSummaryLoading = true;
        this.viewSummaryContent = '';
        this.viewDialogVisible = true;
        this.geotechnicalAnalysisService.getAnalysisSummaryContentById(item._id).subscribe({
            next: (res) => {
                this.viewSummaryContent = res.success && res.data ? (res.data.summary ?? '') : '';
                this.viewSummaryLoading = false;
            },
            error: () => {
                this.viewSummaryLoading = false;
                this.toast.error('Failed to load analysis summary preview');
            },
        });
    }

    /** Opens the rename dialog for the selected analysis summary. */
    onRenameAnalysisSummaryItem(item: GeotechnicalAnalysisSummaryListItem): void {
        this._renameTarget = item;
        this.renameInput = item.name || '';
        this.renameDialogVisible = true;
    }

    /** Persists the new name from the rename dialog. */
    onSubmitRename(): void {
        const target = this._renameTarget;
        const name = this.renameInput.trim();
        if (!target?._id || !name) return;
        this.renameDialogVisible = false;
        this.geotechnicalAnalysisService.renameAnalysisSummary(target._id, name).subscribe({
            next: (res) => {
                if (res.success) {
                    const idx = this.analysisSummaryHistoryList.findIndex((i) => i._id === target._id);
                    if (idx !== -1) this.analysisSummaryHistoryList[idx] = { ...this.analysisSummaryHistoryList[idx], name };
                    this.toast.success('Analysis summary renamed successfully');
                } else {
                    this.toast.error(res.message ?? 'Failed to rename analysis summary');
                }
            },
            error: () => this.toast.error('Failed to rename analysis summary'),
        });
    }

    /** Restores the selected analysis summary as the current one and returns to the main view. */
    onRestoreAnalysisSummaryItem(item: GeotechnicalAnalysisSummaryListItem): void {
        if (!item._id) return;
        this.geotechnicalAnalysisService.restoreAnalysisSummary(item._id).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.latestSummaryDetails._id = res.data._id ?? '';
                    this.latestSummaryDetails.summary = res.data.summary ?? '';
                    this.analysisSummaryStatus = GEOTECHNICAL_SUMMARY_STATUS.COMPLETED;
                    this.openAnalysisSummaryHistory = false;
                    this.toast.success('Analysis summary restored');
                } else {
                    this.toast.error(res.message ?? 'Failed to restore analysis summary');
                }
            },
            error: () => this.toast.error('Failed to restore analysis summary'),
        });
    }

    /** Lazy-loads the Lottie animation JSON for the in-progress state */
    loadGenerationAnimation(): void {
        if (this.generationAnimationData) return;
        fetch('/animations/template_generation.json')
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then(data => (this.generationAnimationData = data))
            .catch(err => console.error('Error loading animation:', err));
    }

    /** Lazy-loads the loading-spinner Lottie JSON for the loading and "In Queue" states */
    loadQueueAnimation(): void {
        if (this.queueAnimationData) return;
        fetch('/animations/loading-spinner.json')
            .then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then(data => (this.queueAnimationData = data))
            .catch(err => console.error('Error loading queue animation:', err));
    }
}
